const base = process.env.REACT_APP_SERVERURL || "";
const api = `${base}/partner-network`;

export type ApiResult<T = any> = { success: boolean; message?: string; code?: string; data: T; status?: number };

const DEVICE_KEY = "hkp.partner.device";

/** A stable id for this phone, so a Mode C link only asks for an OTP on a new device. */
export const deviceId = (): string => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

const request = async <T = any>(url: string, init: RequestInit = {}, auth = true): Promise<ApiResult<T>> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const token = localStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } else {
    headers["X-HKP-Device"] = deviceId();
  }
  try {
    const res = await fetch(url, { ...init, headers: { ...headers, ...(init.headers as Record<string, string>) } });
    const json = await res.json().catch(() => ({}));
    return {
      success: Boolean(json?.success) && res.ok,
      message: json?.message || (res.ok ? "" : "Something went wrong"),
      code: json?.code,
      data: json?.data ?? null,
      status: res.status,
    };
  } catch {
    return { success: false, message: "Could not reach the server.", data: null as any, status: 0 };
  }
};

const send = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

// ---------- Types ----------

export type SharePolicy = Record<string, "AUTO" | "ASK" | "NEVER">;

export type PartnerRow = {
  id: string;
  name: string;
  mode: "HKP_EXISTING" | "SPONSORED" | "LINK_ONLY";
  pendingMode?: string | null;
  status: "INVITED" | "ACTIVE" | "PAUSED" | "ENDED";
  phone?: string;
  language?: string;
  groupIds: string[];
  activeOrders: number;
  health?: string | null;
};

export type Meter = { used: number; included: number; extra: number; limit: number; hasNetwork: boolean };

export type OrderUpdate = {
  id: string;
  kind: string;
  bySide: "PRINCIPAL" | "PARTNER";
  byName?: string;
  channel?: string;
  at: number;
  visibility: "SHARED" | "INTERNAL";
  mine: boolean;
  locked?: boolean;
  payload: { text?: string; stage?: string; piecesDone?: number; files?: { name?: string; url?: string; contentType?: string }[] };
};

export type Offer = { by: string; byName?: string; rate?: number; quantity?: number; dueDate?: number; message?: string; at: number };

export type ProductionOrderView = {
  id: string;
  code: string;
  side: "PRINCIPAL" | "PARTNER";
  title: string;
  productType?: string;
  specifications?: string;
  lines: { colour?: string; quantity?: number; size?: string }[];
  designRefs: { key?: string; name?: string; url?: string; contentType?: string }[];
  dispatch?: { date?: number; courier?: string; tracking?: string; pieces?: number } | null;
  quantity?: number;
  dueDate?: number;
  proposedRate?: number;
  notes?: string;
  status: string;
  offers: Offer[];
  agreedRate?: number;
  agreedQuantity?: number;
  agreedDueDate?: number;
  termsFrozen: boolean;
  turn?: "PRINCIPAL" | "PARTNER" | null;
  stage?: string;
  piecesDone?: number;
  health: string;
  updates: OrderUpdate[];
  readOnly: boolean;
  partnershipStatus?: string;
  // designer only (raw refs, for editing while negotiating)
  principalPrivate?: { clientName?: string; sellingPrice?: number; margin?: number };
  updateSchedule?: any;
  nextNudgeAt?: number;
  partner?: { name?: string; mode?: string; partnershipId?: string };
  // partner only
  designer?: string;
  linkedStudioOrderId?: string;
  sharePolicy?: SharePolicy;
  // Mode C link
  partnerName?: string;
  language?: string;
  ownership?: string;
  preview?: boolean;
};

export type OrderCard = {
  id: string;
  code: string;
  title: string;
  status: string;
  stage?: string;
  dueDate?: number;
  quantity?: number;
  piecesDone?: number;
  rate?: number;
  health: string;
  turn?: string | null;
  partnerName?: string;
  partnerMode?: string;
  partnershipId?: string;
  groupIds?: string[];
  designer?: string;
  lastUpdate?: { kind: string; at: number; text?: string; stage?: string; piecesDone?: number } | null;
};

// ---------- Designer ----------

export const listPartners = () => request<{ partners: PartnerRow[]; meter: Meter }>(`${api}/partners`);
export const lookupPartner = (phone: string, gst: string) =>
  request(`${api}/partners/lookup?phone=${encodeURIComponent(phone)}&gst=${encodeURIComponent(gst)}`);
export const invitePartner = (body: any) => request<{ partner: PartnerRow; inviteUrl?: string }>(`${api}/partners`, send("POST", body));
export const partnerAction = (id: string, action: "pause" | "resume" | "end") =>
  request(`${api}/partners/${id}/${action}`, send("POST"));
export const setPartnerGroups = (id: string, groupIds: string[]) =>
  request<PartnerRow>(`${api}/partners/${id}/groups`, send("PUT", { groupIds }));
export const inviteToWorkspace = (id: string) => request(`${api}/partners/${id}/invite-to-workspace`, send("POST", {}));

export const listProductionOrders = (q: { partnershipId?: string; group?: string; overdue?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (q.partnershipId) params.set("partnershipId", q.partnershipId);
  if (q.group) params.set("group", q.group);
  if (q.overdue) params.set("overdue", "true");
  return request<OrderCard[]>(`${api}/production-orders?${params.toString()}`);
};
export const createProductionOrder = (body: any) =>
  request<{ order: ProductionOrderView; linkUrl?: string }>(`${api}/production-orders`, send("POST", body));
export const presignDesignRef = (fileName: string, contentType: string) =>
  request<{ key: string; uploadUrl: string; contentType: string }>(`${api}/production-orders/design-refs/presign`,
    send("POST", { fileName, contentType }));
export const editProductionOrder = (id: string, body: any) =>
  request<ProductionOrderView>(`${api}/production-orders/${id}`, send("PUT", body));
export const getProductionOrder = (id: string) => request<ProductionOrderView>(`${api}/production-orders/${id}`);
export const saveSchedule = (id: string, body: any) => request<ProductionOrderView>(`${api}/production-orders/${id}/schedule`, send("PUT", body));
export const savePrivate = (id: string, body: any) => request<ProductionOrderView>(`${api}/production-orders/${id}/private`, send("PUT", body));
export const cancelOrder = (id: string) => request<ProductionOrderView>(`${api}/production-orders/${id}/cancel`, send("POST"));
export const revokeLink = (id: string) => request(`${api}/production-orders/${id}/revoke-link`, send("POST"));

// ---------- Both sides ----------

export const counterOffer = (id: string, body: any) => request<ProductionOrderView>(`${api}/production-orders/${id}/offers`, send("POST", body));
export const acceptTerms = (id: string) => request<ProductionOrderView>(`${api}/production-orders/${id}/accept`, send("POST"));
export const postUpdate = (id: string, body: any) => request<ProductionOrderView>(`${api}/production-orders/${id}/updates`, send("POST", body));
export const setVisibility = (id: string, updateId: string, visibility: "SHARED" | "INTERNAL") =>
  request<ProductionOrderView>(`${api}/production-orders/${id}/updates/${updateId}/visibility`, send("PUT", { visibility }));
export const presignFile = (id: string, fileName: string, contentType: string) =>
  request<{ key: string; uploadUrl: string; contentType: string }>(`${api}/production-orders/${id}/files/presign`,
    send("POST", { fileName, contentType }));

// ---------- Partner ----------

export const incomingPartnerships = () => request<any[]>(`${api}/incoming/partnerships`);
export const incomingPartnershipAction = (id: string, action: "accept" | "decline" | "end") =>
  request(`${api}/incoming/partnerships/${id}/${action}`, send("POST"));
export const saveSharePolicy = (id: string, policy: SharePolicy) =>
  request(`${api}/incoming/partnerships/${id}/share-policy`, send("PUT", policy));
export const incomingOrders = () => request<OrderCard[]>(`${api}/incoming/orders`);
export const incomingOrder = (id: string) => request<ProductionOrderView>(`${api}/incoming/orders/${id}`);
export const designerView = (id: string) => request<ProductionOrderView>(`${api}/incoming/orders/${id}/designer-view`);
export const saveDispatch = (id: string, body: { date?: string; courier?: string; tracking?: string; pieces?: number }) =>
  request<ProductionOrderView>(`${api}/incoming/orders/${id}/dispatch`, send("PUT", body));
export const declineOrder = (id: string, reason?: string) =>
  request<ProductionOrderView>(`${api}/incoming/orders/${id}/decline`, send("POST", { reason }));

// ---------- Public (no login) ----------

const pub = `${api}/public`;
export const linkOrder = (token: string) => request<ProductionOrderView>(`${pub}/p/${encodeURIComponent(token)}`, {}, false);
export const linkAccept = (token: string) => request<ProductionOrderView>(`${pub}/p/${encodeURIComponent(token)}/accept`, send("POST"), false);
export const linkCounter = (token: string, body: any) =>
  request<ProductionOrderView>(`${pub}/p/${encodeURIComponent(token)}/counter`, send("POST", body), false);
export const linkUpdate = (token: string, body: any) =>
  request<ProductionOrderView>(`${pub}/p/${encodeURIComponent(token)}/updates`, send("POST", body), false);
export const linkPresign = (token: string, fileName: string, contentType: string) =>
  request<{ key: string; uploadUrl: string }>(`${pub}/p/${encodeURIComponent(token)}/files/presign`,
    send("POST", { fileName, contentType }), false);
export const linkOtpSend = (token: string) => request(`${pub}/p/${encodeURIComponent(token)}/otp/send`, send("POST"), false);
export const linkOtpVerify = (token: string, otp: string) =>
  request<ProductionOrderView>(`${pub}/p/${encodeURIComponent(token)}/otp/verify`, send("POST", { otp }), false);

export const publicInvite = (token: string) => request(`${pub}/invites/${encodeURIComponent(token)}`, {}, false);
export const inviteOtp = (token: string) => request(`${pub}/invites/${encodeURIComponent(token)}/otp`, send("POST"), false);
export const acceptInvite = (token: string, body: any) =>
  request<{ orgId: string; tokens: { token: string; refreshToken: string } }>(
    `${pub}/invites/${encodeURIComponent(token)}/accept`, send("POST", body), false);
export const loginOtpSend = (phone: string) => request(`${pub}/login-otp/send`, send("POST", { phone }), false);
export const loginOtpVerify = (phone: string, otp: string) =>
  request<{ token: string; refreshToken: string }>(`${pub}/login-otp/verify`, send("POST", { phone, otp }), false);

export type DesignRef = { key: string; name: string; contentType: string; preview?: string };

/** Upload design files before the order exists; returns refs to send with the order. */
export const uploadDesignRefs = async (files: FileList | File[]): Promise<{ refs: DesignRef[]; error?: string }> => {
  const refs: DesignRef[] = [];
  for (const file of Array.from(files)) {
    const type = file.type || "image/jpeg";
    const signed = await presignDesignRef(file.name, type);
    if (!signed.success) return { refs, error: signed.message || "Upload not available" };
    if (!(await uploadTo(signed.data.uploadUrl, file, signed.data.contentType))) return { refs, error: "Upload failed. Try again." };
    refs.push({
      key: signed.data.key,
      name: file.name,
      contentType: signed.data.contentType,
      preview: type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
    });
  }
  return { refs };
};

/** Upload a file straight to storage with the signed URL, then return the file ref for an update. */
export const uploadTo = async (uploadUrl: string, file: Blob, contentType: string): Promise<boolean> => {
  try {
    const res = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": contentType } });
    return res.ok;
  } catch {
    return false;
  }
};

export const STAGES = ["ACCEPTED", "STARTED", "DYEING", "WEAVING", "FINISHING", "ON_HOLD", "STUCK", "COMPLETED"] as const;

export const stageLabel = (stage?: string): string =>
  ({
    ACCEPTED: "Accepted",
    STARTED: "Started",
    DYEING: "Dyeing",
    WEAVING: "Weaving",
    FINISHING: "Finishing",
    ON_HOLD: "On hold",
    STUCK: "Stuck",
    COMPLETED: "Completed",
  } as Record<string, string>)[stage || ""] || "Not started";

export const inr = (n?: number | null): string =>
  n === undefined || n === null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;

export const shortDate = (ms?: number | null): string =>
  ms ? new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";
